import { Anchor } from "@mantine/core";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { AuthFlowScaffold } from "../auth/components/AuthFlowScaffold";
import { useAuthFlow } from "../auth/hooks/useAuthFlow";
import { useVerificationAutostart } from "../auth/hooks/useVerificationAutostart";
import { safeReturnTo } from "../auth/rules/returnTo";

/**
 * verificationSearchSchema validates the /verification search params. flow is the
 * verification flow id and code is the one-time code, both carried by the link in
 * the verification email Ory sends after signup (verification is non-blocking, so
 * the user resolves it out-of-band on this route). autostart is the in-app banner's
 * intent flag: when set with no flow/code, the route auto-submits the signed-in
 * user's known email so the user lands straight on the code-entry step.
 */
const verificationSearchSchema = z.object({
  autostart: z.boolean().optional(),
  code: z.string().optional(),
  flow: z.string().optional(),
  return_to: z.string().optional(),
});

/**
 * Route is /verification. The two-step UI (enter email → enter the emailed code) is
 * driven entirely by Ory's re-rendered nodes: submitting the email re-renders the
 * flow with a code input + a "code sent" notice. Opened from the in-app verify-email
 * banner (autostart, no flow/code), the route auto-submits the known email so the
 * user lands straight on the code step. Cross-link to login.
 */
export const Route = createFileRoute("/_auth/verification")({
  component: VerificationRoute,
  validateSearch: verificationSearchSchema,
});

function VerificationRoute() {
  const { t } = useTranslation("iam");
  const search = Route.useSearch();
  const state = useAuthFlow("verification", {
    defaultRedirect: safeReturnTo(search.return_to),
    flowId: search.flow,
  });

  // Auto-submit the known email only for an in-app banner open (autostart with no
  // flow/code). An email-link entry (?flow&code) and a manual visit leave it off, so
  // they show Ory's nodes untouched. While the autostart is pending (the flow is still
  // on the email step the auto-submit is advancing) the column shows the loader instead
  // of the email form, so the user only ever sees the code step, not a flash of step 1.
  const pendingAutostart = useVerificationAutostart({
    enabled: Boolean(search.autostart) && !search.flow && !search.code,
    flow: state.flow,
    submit: state.submit,
  });

  // The pending autostart keeps the column on the loader, folded into the scaffold's
  // own readiness gate so the heading and cross-links stay hidden until the code step
  // is the first thing the user sees.
  const gatedState = { ...state, isLoading: state.isLoading || pendingAutostart };

  return (
    <AuthFlowScaffold
      state={gatedState}
      subtitle={t("verification.subtitle")}
      title={t("verification.title")}
    >
      <Anchor component={Link} size="sm" to="/login">
        {t("verification.backToSignIn")}
      </Anchor>
    </AuthFlowScaffold>
  );
}
